import { useCallback } from 'react';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  LabelValueDetailsInput,
  TimeSignalClassification,
  UpdateTimeSignalsInput,
  UpdateTimeSignalsMutation,
  useUpdateTimeSignalsMutation,
  TimeSignalFilterInput,
  TimeSignal,
  usePutLabelSuggestionsMutation,
  LabelType,
  CreateTimeSignalMutation,
  PutLabelSuggestionsMutation,
  LabelSuggestionDetails
} from 'graphql/types';
import { useMeContext } from 'modules/me/MeContextProvider';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { useNotesToastContext } from '../NotesToastContextProvider';
import {
  NotesClassification,
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { TIMELINE_TIMESIGNAL_COUNT } from '../Timeline';
import {
  updateCachedTimeSignalsWithFederatedSuggestions,
  updateTimeSignalsCache
} from '../cache/utils/cache-utils';
import { mapTimesheetLabel, truncateMessage } from '../common/utils';
import { clearCachedDates } from '../cache/utils/time-signal-utils';
import { mapDateToISOString } from '../../../utils/date-time';
import { TimesheetLabel } from '../../timesheet-labels/labelSchema';
import {
  useTimeSignalGetParam,
  TimeSignalGetParam,
  useTimeSignalUpdateFilter,
  UpdateFilterParams
} from './useTimeSignalParam';

const updateCacheWithFederatedSuggestions = (param: TimeSignalGetParam) => (
  cache: ApolloCache<CreateTimeSignalMutation>,
  mutationResult: FetchResult<
    PutLabelSuggestionsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.putLabelSuggestions) {
    updateCachedTimeSignalsWithFederatedSuggestions(
      cache,
      mutationResult?.data?.putLabelSuggestions,
      param
    );
  }
};

export const updateCache = (
  param: TimeSignalGetParam,
  selectedClassification: NotesClassification,
  classificationTarget: TimeSignalClassification,
  useLabelAcceptedBy: boolean
) => (
  cache: ApolloCache<UpdateTimeSignalsMutation>,
  mutationResult: FetchResult<
    UpdateTimeSignalsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.updateTimeSignals?.items) {
    updateTimeSignalsCache(
      cache,
      mutationResult?.data?.updateTimeSignals?.items as TimeSignal[],
      param,
      selectedClassification,
      classificationTarget,
      useLabelAcceptedBy
    );
  }
};

type BulkUpdateClassificationArgs = {
  selectedNoteIds: Array<string>;
  unSelectedNoteIds: Array<string>;
  classificationTarget: TimeSignalClassification;
  note?: TimeSignal;
};

type OnMultiEditTimeSignalsArgs = {
  notes: TimeSignal[];
  classificationTarget: TimeSignalClassification;
  labelValues: TimesheetLabel[];
};

type FilterInputArgs = {
  selectedNoteIds: Array<string>;
  unSelectedNoteIds: Array<string>;
  classificationTarget: TimeSignalClassification;
  labelValues?: LabelValueDetailsInput[];
  cursor: string | null;
  selectedFilterFn: (params: UpdateFilterParams) => TimeSignalFilterInput;
};

const getFilterInput = ({
  selectedNoteIds,
  unSelectedNoteIds,
  classificationTarget,
  labelValues,
  cursor,
  selectedFilterFn
}: FilterInputArgs) => {
  const selectionFilter = selectedFilterFn({
    includeIds: selectedNoteIds.length ? selectedNoteIds : undefined,
    excludeIds: unSelectedNoteIds.length ? unSelectedNoteIds : undefined
  });

  const filterInput: UpdateTimeSignalsInput = {
    selectionFilter,
    updates: {
      classification: classificationTarget,
      labelValues
    },
    cursor
  };
  return filterInput;
};

export const getMoveMessage = (
  classificationTarget: TimeSignalClassification,
  note?: TimeSignal
): string =>
  note
    ? `toast.note.classification.singleMove.${classificationTarget.toLocaleLowerCase()}`
    : `toast.note.classification.bulkMove.${classificationTarget.toLocaleLowerCase()}`;

//Todo: Remove this when time-signal-federated-suggestions FF is rolled out (TIDP-1469)
export const useBulkUpdateTimeSignals = (): {
  onTimeSignalClassificationUpdate: (
    args: BulkUpdateClassificationArgs
  ) => Promise<void>;
  onMultiEditTimeSignals: (args: OnMultiEditTimeSignalsArgs) => Promise<void>;
  updateInProgress: boolean;
} => {
  const selectedFilterFn = useTimeSignalUpdateFilter();
  const {
    filterContext: { pageType }
  } = useNotesFilterContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();

  const [
    bulkUpdateTimeSignals,
    { loading: bulkUpdateLoading, error: bulkUpdateError }
  ] = useUpdateTimeSignalsMutation();
  const [
    putSuggestions,
    { loading: putSuggestionsLoading, error: putSuggestionsError }
  ] = usePutLabelSuggestionsMutation();
  const { setToastContext } = useNotesToastContext();
  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();
  const { id } = useMeContext() ?? {};
  const onTimeSignalClassificationUpdate = useCallback(
    async (args: BulkUpdateClassificationArgs) => {
      const { selectedNoteIds, classificationTarget } = args;
      let result = null;
      clearCachedDates(id);
      do {
        const input = getFilterInput({
          ...args,
          cursor: result?.data?.updateTimeSignals?.cursor ?? null,
          selectedFilterFn
        });

        try {
          result = await bulkUpdateTimeSignals({
            variables: {
              input
            },
            update: updateCache(
              param,
              selectedClassification,
              classificationTarget,
              isSmartSuggestionEnabled
            ),
            awaitRefetchQueries: false,
            refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
          });
        } catch (e) {
          setToastContext({
            showError: true,
            message: 'toast.note.failure'
          });
        }
      } while (result?.data?.updateTimeSignals?.cursor);

      if (classificationTarget && !bulkUpdateLoading && !bulkUpdateError) {
        setToastContext({
          showSuccess: true,
          message: getMoveMessage(classificationTarget, args.note),
          values: {
            message: truncateMessage(args.note?.description ?? ''),
            count: `${selectedNoteIds.length}`
          }
        });
      }
    },
    [
      bulkUpdateLoading,
      bulkUpdateError,
      selectedFilterFn,
      bulkUpdateTimeSignals,
      param,
      selectedClassification,
      id,
      setToastContext,
      isSmartSuggestionEnabled
    ]
  );

  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const onMultiEditTimeSignals = useCallback(
    async ({
      notes,
      labelValues,
      classificationTarget
    }: OnMultiEditTimeSignalsArgs) => {
      let bulkUpdateResult = null;
      clearCachedDates(id);

      const cptLabels = [
        LabelType.Client,
        LabelType.Project,
        LabelType.Program,
        LabelType.Task,
        LabelType.Billing
      ];
      const isCPTUpdated = labelValues.some(
        (lv: TimesheetLabel) => lv.value && cptLabels.includes(lv.type)
      );
      const labelsToUpdate = labelValues.filter((lv: TimesheetLabel) => {
        return lv.value || (isCPTUpdated ? cptLabels.includes(lv.type) : false);
      });
      const labelsToUpdateIds = labelsToUpdate.map(
        (lv: TimesheetLabel) => lv.id
      );

      if (isTimeSignalFederatedSuggestionsEnabled) {
        const updateSuggestionPromises = notes.map(async (note: TimeSignal) => {
          const keptFederatedSuggestions = note.suggestions.suggestions.filter(
            (suggestion: LabelSuggestionDetails) => {
              return (
                !labelsToUpdateIds.includes(suggestion.label.id) &&
                (isCPTUpdated
                  ? !cptLabels.includes(suggestion.label.type)
                  : true)
              );
            }
          );
          if (note?.id && id && note?.labelValuesInferenceId) {
            await putSuggestions({
              variables: {
                input: {
                  userUri: id,
                  inferenceId: note.labelValuesInferenceId,
                  timeSignalId: note.id,
                  startTime: mapDateToISOString(note.startTime),
                  endTime: mapDateToISOString(note.endTime),
                  duration: note.duration ?? null,
                  suggestions: keptFederatedSuggestions.map(
                    (suggestion: LabelSuggestionDetails) =>
                      ({
                        labelId: suggestion.label.id,
                        suggestionMetadata:
                          suggestion.suggestionMetadata ?? null,
                        thresholdMet: suggestion.thresholdMet ?? false,
                        suggestedValues:
                          suggestion?.suggestedValues?.map((sv) => ({
                            idValue: sv?.idValue,
                            stringValue: sv?.stringValue,
                            numericValue: sv?.numericValue
                          })) ?? []
                      } as any)
                  )
                }
              },
              update: updateCacheWithFederatedSuggestions(param)
            });
          }
        });
        await Promise.all(updateSuggestionPromises);
      }

      const labelValueInputs = labelsToUpdate.map(
        (labelValue: TimesheetLabel) =>
          mapTimesheetLabel(isSmartSuggestionEnabled, labelValue)
      );
      do {
        const input = getFilterInput({
          selectedNoteIds: notes.map((note) => note.id),
          unSelectedNoteIds: [],
          classificationTarget: classificationTarget,
          labelValues: labelValueInputs,
          cursor:
            (bulkUpdateResult as any)?.data?.updateTimeSignals.cursor ?? null,
          selectedFilterFn
        });
        bulkUpdateResult = await bulkUpdateTimeSignals({
          variables: {
            input
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            'timeSignals',
            'timeSignalsCount',
            'timeSignalsTotals'
          ],
          update: updateCache(
            param,
            selectedClassification,
            classificationTarget,
            isSmartSuggestionEnabled
          )
        });
      } while ((bulkUpdateResult as any)?.data?.updateTimeSignals?.cursor);

      if (
        !bulkUpdateLoading &&
        !bulkUpdateError &&
        (isTimeSignalFederatedSuggestionsEnabled
          ? !putSuggestionsLoading && !putSuggestionsError
          : true)
      ) {
        setToastContext({
          showSuccess: true,
          message: 'note.multiEdit.nNotesUpdated',
          values: {
            count: `${notes.length}`
          }
        });
      } else if (
        isTimeSignalFederatedSuggestionsEnabled &&
        (bulkUpdateError || putSuggestionsError)
      ) {
        setToastContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }
    },
    [
      id,
      isTimeSignalFederatedSuggestionsEnabled,
      bulkUpdateLoading,
      bulkUpdateError,
      putSuggestionsLoading,
      putSuggestionsError,
      putSuggestions,
      param,
      isSmartSuggestionEnabled,
      selectedFilterFn,
      bulkUpdateTimeSignals,
      selectedClassification,
      setToastContext
    ]
  );

  return {
    onTimeSignalClassificationUpdate,
    onMultiEditTimeSignals,
    updateInProgress:
      bulkUpdateLoading ||
      (isTimeSignalFederatedSuggestionsEnabled ? putSuggestionsLoading : false)
  };
};
